import React, { useRef, useState } from "react";
import JsonEditor from "../components/JsonEditor";

const Home = ({ darkMode }) => {
  const [errorDetails, setErrorDetails] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [isCompressedMode, setIsCompressedMode] = useState(false);
  const editorRef = useRef(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2000);
  };

  const handlePrettyPrint = () => {
    const editor = editorRef.current;
    if (!editor) return;

    try {
      const raw = editor.getValue();
      const formatted = JSON.stringify(JSON.parse(raw), null, 2);
      editor.setValue(formatted);
      setErrorDetails({ valid: true });
      showToast("Pretty printed");
    } catch (err) {
      setErrorDetails({ valid: false, message: `Pretty failed: ${err.message}` });
      showToast("Pretty failed");
    }
  };

  const handleCompressJSON = () => {
    const editor = editorRef.current;
    if (!editor) return;

    try {
      const text = editor.getValue();
      const compressed = JSON.stringify(JSON.parse(text));
      const escaped = compressed.replace(/"/g, '\\"');
      editor.setValue(escaped);
      setErrorDetails({ valid: true });
      showToast("JSON compressed");
    } catch (err) {
      setErrorDetails({ valid: false, message: `Compression failed: ${err.message}` });
      showToast("Compression failed");
    }
  };

  const handleDecompressJSON = () => {
    const editor = editorRef.current;
    if (!editor) return;

    try {
      const compressedText = editor.getValue();
      const unescaped = compressedText.replace(/\\"/g, '"');
      const decompressed = JSON.stringify(JSON.parse(unescaped), null, 2);
      editor.setValue(decompressed);
      setErrorDetails({ valid: true });
      showToast("JSON decompressed");
    } catch (err) {
      setErrorDetails({ valid: false, message: `Decompression failed: ${err.message}` });
      showToast("Decompression failed");
    }
  };

  const handleCopyToClipboard = () => {
    const editor = editorRef.current;
    if (!editor) return;

    try {
      const text = editor.getValue();
      navigator.clipboard.writeText(text).then(() => {
        showToast("Copied to clipboard");
      });
    } catch {
      showToast("Copy failed");
    }
  };

  return (
    <div className="flex flex-col items-center px-4 py-8">
      {/* Toast Notification */}
      {toastMsg && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-sm px-4 py-2 rounded shadow-lg z-50">
          {toastMsg}
        </div>
      )}

      {/* Title & Description */}
      <h1 className="text-4xl font-bold text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text mb-2">
        JSON Validator and Formatter
      </h1>
      <p className="text-gray-400 dark:text-gray-500 text-center max-w-md mb-6">
    Validate, format, compress, and decompress your JSON effortlessly. This tool runs 100% in your browser—your data stays private and never leaves your device.
      </p>

      {/* Editor - Fixed sizing that works */}
      <div className="relative w-full max-w-6xl h-[500px] md:h-[600px] shadow-lg rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
        <JsonEditor
          setErrorDetails={setErrorDetails}
          darkMode={darkMode}
          editorRef={editorRef}
        />
        <button
          onClick={handleCopyToClipboard}
          className="absolute top-2 right-2 bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-1 rounded shadow transition-colors"
          title="Copy JSON to clipboard (Ctrl+C also works in editor)"
          aria-label="Copy JSON to clipboard"
        >
          Copy
        </button>
      </div>

      {/* Validation Banner */}
      {errorDetails && (
        <div className={`mt-4 px-4 py-2 w-full max-w-6xl text-center rounded ${
          errorDetails.valid ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200" : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200"
        }`}>
          {errorDetails.message || (errorDetails.valid ? "Valid JSON" : "Invalid JSON")}
        </div>
      )}

      {/* Buttons */}
      <div className="mt-6 mb-8 flex flex-wrap justify-center gap-4">
        <button
          onClick={handlePrettyPrint}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2 rounded shadow transition-colors"
          title="Format JSON with proper indentation"
          aria-label="Pretty print JSON"
        >
          Pretty Print
        </button>
        <button
          onClick={() => {
            if (isCompressedMode) {
              handleDecompressJSON();
            } else {
              handleCompressJSON();
            }
            setIsCompressedMode(!isCompressedMode);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2 rounded shadow transition-colors"
          title={isCompressedMode ? "Decompress escaped JSON string" : "Compress and escape JSON"}
          aria-label={isCompressedMode ? "Decompress JSON" : "Compress JSON"}
        >
          {isCompressedMode ? "Decompress" : "Compress"}
        </button>
      </div>

   <section className="text-gray-700 dark:text-gray-300 text-center max-w-2xl mx-auto mb-6">
  <h2 className="text-xl font-semibold mt-6 mb-2">Why use this JSON tool?</h2>
  <ul className="list-disc list-inside text-left mb-3">
    <li>Quickly check if your JSON is valid before using it in APIs or applications.</li>
    <li>Format (pretty print) JSON for easier reading and debugging.</li>
    <li>Compress JSON to a single line for compact storage or transmission.</li>
    <li>Decompress/escape JSON strings for editing or review.</li>
  </ul>
  <h2 className="text-xl font-semibold mt-6 mb-2">How to use this tool:</h2>
  <ol className="list-decimal list-inside text-left mb-3">
    <li>Paste or type your JSON data into the editor below.</li>
    <li>Use the <b>Pretty Print</b> button to format, or <b>Compress</b>/<b>Decompress</b> as needed.</li>
    <li>Copy the result to your clipboard for use elsewhere.</li>
  </ol>
  <h2 className="text-xl font-semibold mt-6 mb-2">Example</h2>
  <div className="bg-gray-100 dark:bg-gray-800 rounded p-3 text-left text-sm mb-3 overflow-x-auto">
    <b>Input JSON:</b>
    <pre className="whitespace-pre-wrap">{`{"name": "John", "age": 30, "city": "New York"}`}</pre>
    <b>Pretty Printed Output:</b>
    <pre className="whitespace-pre-wrap">{`{
  "name": "John",
  "age": 30,
  "city": "New York"
}`}</pre>
  </div>
  <p className="mt-4">Need help? See our <a href="/about" className="text-blue-600 underline">About</a> page or contact us for support.</p>
</section>
    </div>


  );
};

export default Home;
